import { WindowProxy } from '@sm360/widget-commons-core-services';
import _ from 'lodash';
import { VEHICLE_CONDITION } from 'commons-vehicle/constants/vehicle.js';
import COLANDER_GROUPS from 'commons-general/constants/colanderGroups.js';
import { SD_STATUS } from './constants.js';

/* eslint-disable no-underscore-dangle */
class ShiftDigitalService {
    hasShiftDigital() {
        return !!(WindowProxy.get() && WindowProxy.get().sd);
    }

    createFormEvent(leadId, formType) {
        if (this.hasShiftDigital()) {
            WindowProxy.get().sd('dataLayer', {
                leadId,
                formType,
                events: 'formSubmission',
            });
            WindowProxy.get().sd('send');
        }
    }

    // PageView event is meant only to trigger for page View without any data layer properties
    createPageViewEvent() {
        if (this.hasShiftDigital()) {
            WindowProxy.get().sd('dataLayer', {});
            WindowProxy.get().sd('send', 'pageview');
        }
    }

    updateListingEvent(criteria, metadata, vehicleCount, bodyStyles, colanderGroupSlug) {
        let bodyStyle = 'ALL';
        let make = 'ALL';
        let model = 'ALL';
        if (this.hasShiftDigital()) {
            const minYear = criteria?.yearRange?.from || 'ALL';
            const maxYear = criteria?.yearRange?.to || 'ALL';
            const minPrice = criteria?.salePriceRange?.from || 'ALL';
            const maxPrice = criteria?.salePriceRange?.to || 'ALL';
            const searchBar = criteria?.textQuery || '';
            const currentStatus = colanderGroupSlug === COLANDER_GROUPS.NEW ? SD_STATUS.NEW : SD_STATUS.USED;

            if (criteria?.frameStyleTypes?.length > 0) {
                bodyStyle = this.getCurrentBodyStyles(criteria.frameStyleTypes, bodyStyles)?.join();
            }

            if (criteria?.makeIds?.length > 0) {
                make = this.getCurrentMeta(criteria.makeIds, metadata, 'makeId')?.join();
            }

            if (criteria?.modelIds?.length > 0) {
                model = this.getCurrentMeta(criteria.modelIds, metadata, 'modelId')?.join();
            }

            WindowProxy.get().sd('dataLayer', {
                countSearchResults: vehicleCount,
                filterSearch: {
                    status: currentStatus,
                    minYear,
                    maxYear,
                    make,
                    model,
                    minPrice,
                    maxPrice,
                    bodyStyle,
                    searchBar,
                },
                events: 'filterSearch',
            });
            WindowProxy.get().sd('send');
        }
    }

    updateTypedSearchEvent(criteria) {
        if (this.hasShiftDigital()) {
            const searchBar = criteria?.textQuery || '';
            if (!_.isEmpty(searchBar)) {
                WindowProxy.get().sd('dataLayer', {
                    typedSearch: {
                        typedSearchContent: searchBar,
                    },
                    events: 'typedSearch',
                });
                WindowProxy.get().sd('send');
            }
        }
    }

    updateTradeInEvent(quoteId, eventName, vehicle) {
        const dealId = quoteId;
        let vehicleYear = '';
        let vehicleMake = '';
        let vehicleModel = '';
        let vehicleVin = '';
        let vehicleStatus = '';

        if (this.hasShiftDigital()) {
            if (vehicle) {
                vehicleYear = vehicle.year;
                vehicleMake = vehicle.make;
                vehicleModel = vehicle.model;
                vehicleVin = vehicle.vin;
                if (vehicle.vehicleConditionType === 256) {
                    vehicleStatus = 'Used';
                } else if (vehicle.vehicleConditionType === 768) {
                    vehicleStatus = 'CPO';
                } else {
                    vehicleStatus = 'New';
                }
            }

            WindowProxy.get().sd('dataLayer', {
                digRet: {
                    dealId,
                    provider: 'SM360',
                    vehicleYear,
                    vehicleMake,
                    vehicleModel,
                    vehicleVin,
                    vehicleStatus,
                },
                events: eventName,
            });
            WindowProxy.get().sd('send');
        }
    }

    updateCommonEvent(quoteId, eventName, vehicle) {
        const dealId = quoteId;
        const provider = 'SM360';
        let vehicleStatus = '';
        let vehicleYear = '';
        let vehicleMake = '';
        let vehicleModel = '';
        let vehicleVin = '';

        if (this.hasShiftDigital()) {
            if (vehicle && vehicle._map && vehicle._map._root && vehicle._map._root.nodes) {
                const nodes = vehicle._map._root.nodes;

                const nodesData = _.reduce(
                    nodes,
                    (acc, value) => {
                        if (_.get(value, 'entry')) {
                            acc.push(value.entry);
                        } else if (_.get(value, 'nodes')) {
                            _.forEach(value.nodes, (nvalue) => {
                                if (_.get(nvalue, 'nodes')) {
                                    _.forEach(nvalue.nodes, (nv) => {
                                        acc.push(nv.entry);
                                    });
                                } else {
                                    acc.push(nvalue.entry);
                                }
                            });
                        }
                        return acc;
                    },
                    []
                );

                _.forEach(nodesData, (node) => {
                    if (node) {
                        const currentColander = node[0];
                        let currentValue = node[1];

                        if (typeof currentValue === 'object') {
                            currentValue = _.get(node[1], 'name');
                        }
                        if (currentColander === 'year') {
                            vehicleYear = currentValue;
                        }
                        if (currentColander === 'make') {
                            vehicleMake = currentValue;
                        }
                        if (currentColander === 'model') {
                            vehicleModel = currentValue;
                        }
                        if (currentColander === 'vin') {
                            vehicleVin = currentValue;
                        }
                        if (currentColander === 'vehicleConditionType') {
                            switch (currentValue) {
                                case 256:
                                    vehicleStatus = 'Used';
                                    break;
                                case 768:
                                    vehicleStatus = 'CPO';
                                    break;
                                default:
                                    vehicleStatus = 'New';
                            }
                        }
                        if (currentColander === 'vehicleCondition') {
                            switch (currentValue) {
                                case VEHICLE_CONDITION.USED:
                                    vehicleStatus = 'Used';
                                    break;
                                case VEHICLE_CONDITION.CERTIFIED:
                                    vehicleStatus = 'CPO';
                                    break;
                                case VEHICLE_CONDITION.DEMO:
                                    vehicleStatus = 'Demo';
                                    break;
                                case VEHICLE_CONDITION.NEW:
                                    vehicleStatus = 'New';
                                    break;
                                default:
                                    vehicleStatus = currentValue;
                            }
                        }
                    }
                });
            }
            WindowProxy.get().sd('dataLayer', {
                digRet: {
                    dealId,
                    provider,
                    vehicleStatus,
                    vehicleYear,
                    vehicleMake,
                    vehicleModel,
                    vehicleVin,
                },
                events: eventName,
            });
            WindowProxy.get().sd('send');
        }
    }

    updateCatalogEvent(filters) {
        let bodyStyle = 'ALL';
        let make = 'ALL';
        let searchBar = '';

        if (this.hasShiftDigital()) {
            if (filters._map && filters._map._root && (filters._map._root.entries || filters._map._root.nodes)) {
                let entries = filters?._map?._root?.entries;

                // If 'entries' field is not available, use 'nodes' field
                if (!entries && filters?._map?._root?.nodes)
                    entries = filters._map._root.nodes.map((node) => node.entry).filter((x) => !!x);

                entries.forEach((entry) => {
                    const currentColander = entry[0];
                    const currentValue = entry[1];
                    if (currentColander === 'preferredMake') {
                        make = currentValue;
                    }
                    if (currentColander === 'modelSearchString') {
                        searchBar = currentValue;
                    }
                    if (currentColander === 'bodyStylesFilterCodes' && currentValue && currentValue.length > 0) {
                        bodyStyle = Array.isArray(currentValue) ? currentValue.join() : currentValue;
                    }
                });
                if (!_.isEmpty(searchBar)) {
                    WindowProxy.get().sd('dataLayer', {
                        typedSearch: {
                            typedSearchContent: searchBar,
                        },
                        events: 'typedSearch',
                    });
                } else {
                    WindowProxy.get().sd('dataLayer', {
                        filterSearch: {
                            make,
                            bodyStyle,
                        },
                        events: 'filterSearch',
                    });
                }
                WindowProxy.get().sd('send');
            }
        }
    }

    getCurrentMeta(currentValues, metadata, metaName) {
        const value = [];
        if (currentValues?.length > 0) {
            metadata.filter((meta) => meta.name === metaName).some((meta) => {
                _.forEach(currentValues, (currentValue) => {
                    const metadatas = meta.metadatas.filter((data) => data.code === currentValue);
                    if (metadatas && !_.isEmpty(metadatas)) {
                        value.push(metadatas[0].label);
                    }
                });
                return false;
            });
        }
        return value;
    }

    getCurrentBodyStyles(currentValues, bodyStyles) {
        const value = [];
        if (currentValues?.length > 0) {
            _.forEach(currentValues, (currentValue) => {
                const bodyStyle = bodyStyles.filter((style) => style.codes.includes(currentValue))?.[0];
                value.push(bodyStyle?.group);
            });
        }
        return value;
    }

    updateDropSaveEvent(quotes) {
        _.forEach(quotes, (quote) => {
            if (quote.status === 'quote') {
                this.updateCommonEvent(quote.id, 'drDropSave', quote.vehicle);
            }
        });
    }
}

export default new ShiftDigitalService();
